/**
 * Factory for the singleton QueryClient used by the SPA.
 *
 * React Query is the only cache. Data loaded during a session stays in
 * memory; navigating between pages does not refetch. Refresh the browser
 * to fetch fresh data.
 *
 *   - `staleTime: Infinity` — data fetched once stays "fresh" for the
 *     whole session. No automatic refetch on mount, focus, or interval.
 *     User refreshes the page to get fresh data.
 *   - `gcTime: 30 * 60_000` — 30 minutes. Bump up RAM if needed.
 *   - `refetchOnWindowFocus: false` — focus-refetch is redundant once
 *     `staleTime: Infinity` is in place.
 *   - `retry: 1` — one quick retry, no exponential storm on the backend.
 */
import { QueryClient } from '@tanstack/react-query';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Infinity,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}
